import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { MobXProviderContext } from "mobx-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import { Toast } from "primereact/toast";
import { ROLE_TYPE_ADMIN, ROLE_TYPE_MHP, ROLE_TYPE_NURSE, ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import Unauthorized from "../../../shared/pages/Unauthorized";
import { RouterAddress } from "../../../constants/RouterAddress";
import { AI_MESSAGE_FOR_COMPLIANCE, getErrorTitle, JOB_STATUS, USER_GROUP_ADMIN } from "../../../constants/constants";
import { ReminderConstants } from "../../../constants/reminderConstants";
import { openUrlInNewTab } from "../../../utils/securityUtils";
import { dashboardPageContainerStyle, renderDashboardHeader } from "../../dashboard/pages/dashboardStyles";
import { appInnerCardStyle, appMutedTextStyle, appSectionCardStyle } from "../../../components/general/uiSurfaceStyles";
import { ensureResidentBinderBundle, getBinderFolderByCode, RESIDENT_BINDER_FOLDER_CODES, upsertBinderDocumentWithVersion } from "../../../utils/binderFeatureUtils";

const PODIATRY_CONSENT = "PODIATRY_CONSENT";
const PODIATRY_TRACKER = "PODIATRY_TRACKER";
const FOOT_NAIL_CARE_TITLE = "Foot & Nail Care";
const LOS_ANGELES_TIMEZONE = "America/Los_Angeles";
const TODAY_IN_LOS_ANGELES = "2026-08-13";
type FootView = "left_top" | "left_bottom" | "right_top" | "right_bottom";
type FootFindingCode = "C" | "D" | "R" | "U" | "PU" | "F" | "M" | "S" | "T";
type FootFindingMarker = {
  id: string;
  view: FootView;
  x: number;
  y: number;
  findingCode: FootFindingCode;
};

const FOOT_VIEW_OPTIONS: Array<{ view: FootView; title: string; imageSrc: string }> = [
  { view: "left_top", title: "Left Foot Top", imageSrc: "/podiatry/foot-left-top.png" },
  { view: "right_top", title: "Right Foot Top", imageSrc: "/podiatry/foot-right-top.png" },
  { view: "left_bottom", title: "Left Foot Bottom", imageSrc: "/podiatry/foot-left-bottom.png" },
  { view: "right_bottom", title: "Right Foot Bottom", imageSrc: "/podiatry/foot-right-bottom.png" },
];

const FOOT_FINDING_OPTIONS: Array<{ code: FootFindingCode; label: string; color: string; textColor: string }> = [
  { code: "C", label: "Callus", color: "#f59e0b", textColor: "#111827" },
  { code: "D", label: "Dryness", color: "#38bdf8", textColor: "#082f49" },
  { code: "R", label: "Redness", color: "#ef4444", textColor: "#ffffff" },
  { code: "U", label: "Ulcer", color: "#7c3aed", textColor: "#ffffff" },
  { code: "PU", label: "Pre Ulcer", color: "#a855f7", textColor: "#ffffff" },
  { code: "F", label: "Fissure", color: "#f97316", textColor: "#ffffff" },
  { code: "M", label: "Maceration", color: "#14b8a6", textColor: "#ffffff" },
  { code: "S", label: "Swelling", color: "#22c55e", textColor: "#052e16" },
  { code: "T", label: "Tinea", color: "#6366f1", textColor: "#ffffff" },
];

const FINDING_LABEL_BY_CODE: Record<FootFindingCode, string> = {
  C: "C - Callus",
  D: "D - Dryness",
  R: "R - Redness",
  U: "U - Ulcer",
  PU: "PU - Pre Ulcer",
  F: "F - Fissure",
  M: "M - Maceration",
  S: "S - Swelling",
  T: "T - Tinea",
};

const emitPodiatristActionLoading = (action: string, loading: boolean) => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("resident-podiatrist:action-loading", {
      detail: {
        action,
        loading,
      },
    }));
  }
};

const formMeta: Record<string, { title: string; description: string }> = {
  [PODIATRY_CONSENT]: {
    title: "Consent Form",
    description: "Consent, contact, payment, and supporting resident details.",
  },
  [PODIATRY_TRACKER]: {
    title: "Tracker",
    description: "Visit concerns, treatment details, observations, and follow-up.",
  },
};

const resolveFormOptions = (values: any, fallbacks: string[] = []) => {
  const sourceValues = Array.isArray(values) ? values : [];
  const normalized = sourceValues
    .map((item: any) => String(item || "").trim())
    .filter(Boolean)
    .map((label) => ({ label, value: label }));
  if (normalized.length > 0) {
    return normalized;
  }
  return fallbacks.map((label) => ({ label, value: label }));
};

const normalizeSelectedValues = (value: any) => (
  Array.isArray(value)
    ? value.map((item) => String(item || "").trim()).filter(Boolean)
    : []
);

const parseDateOnlyValue = (value: any): Date | null => {
  const normalized = String(value || "").trim();
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0, 0);
};

const formatDateOnlyValue = (value: Date | Date[] | null | undefined) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return "";
  }
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, "0");
  const day = `${value.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateTimeDisplay = (value: any) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "Not available";
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return normalized;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: LOS_ANGELES_TIMEZONE,
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
};

const formatDateOnlyDisplay = (value: any) => {
  const parsed = parseDateOnlyValue(value);
  if (!parsed) {
    return "Not set";
  }
  return new Intl.DateTimeFormat("en-US", {
    timeZone: LOS_ANGELES_TIMEZONE,
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  }).format(parsed);
};

const formatComparisonValue = (value: any) => {
  if (Array.isArray(value)) {
    const normalized = value.map((item) => String(item || "").trim()).filter(Boolean);
    return normalized.length > 0 ? normalized.join(", ") : "Not documented";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  const normalized = String(value ?? "").trim();
  return normalized || "Not documented";
};

const hasMeaningfulComparisonValue = (value: any): boolean => {
  if (Array.isArray(value)) {
    return value.some((item) => String(item || "").trim());
  }
  if (typeof value === "boolean") {
    return value;
  }
  return String(value ?? "").trim().length > 0;
};

const NurseResidentPodiatristPage = () => {
  const { residentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { authStore, backendAPIStore } = useContext(MobXProviderContext);
  const toast = useRef<Toast>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloadingDocument, setDownloadingDocument] = useState(false);
  const [savingToBinder, setSavingToBinder] = useState(false);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [creatingDrafts, setCreatingDrafts] = useState(false);
  const [forms, setForms] = useState<any[]>([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [selectedFormId, setSelectedFormId] = useState("");
  const [selectedFormHeader, setSelectedFormHeader] = useState<any | null>(null);
  const [formData, setFormData] = useState<any>({});
  const [previousVisitComparison, setPreviousVisitComparison] = useState<any | null>(null);
  const [currentUserFullName, setCurrentUserFullName] = useState("");
  const [currentNurseContactDetails, setCurrentNurseContactDetails] = useState("");
  const [currentNurseEmailAddress, setCurrentNurseEmailAddress] = useState("");
  const [activeFootFindingCode, setActiveFootFindingCode] = useState<FootFindingCode>("C");
  const [quickView, setQuickView] = useState<any>({
    residentName: "",
    facilityName: "",
    diagnosisText: "",
    allergies: "",
    carePlanExists: false,
  });
  const [showCarePlanRequiredDialog, setShowCarePlanRequiredDialog] = useState(false);
  const [showGenerationJobDialog, setShowGenerationJobDialog] = useState(false);
  const [generationJobDetails, setGenerationJobDetails] = useState<any>(null);
  const [generationProcessingTime, setGenerationProcessingTime] = useState(0);
  const [creatingVersion, setCreatingVersion] = useState(false);
  const [showFollowUpReminderDialog, setShowFollowUpReminderDialog] = useState(false);
  const [creatingFollowUpReminder, setCreatingFollowUpReminder] = useState(false);
  const [followUpReminderConfig, setFollowUpReminderConfig] = useState({
    title: "",
    description: "",
    frequency: ReminderConstants.FREQUENCY_EVERY_MONTH,
    earlyWeeks: ReminderConstants.EARLY_REMINDER_1_WEEK,
  });

  const userRoleType = authStore.getUserRoleType();
  const isAdminUser = (authStore.getUserGroups() || []).includes(USER_GROUP_ADMIN) || userRoleType === ROLE_TYPE_ADMIN;
  const isSharedResidentMode = location.pathname.startsWith("/shared/home/");
  const canAccess = isAdminUser || userRoleType === ROLE_TYPE_NURSE || userRoleType === ROLE_TYPE_MHP || userRoleType === ROLE_TYPE_PROVIDER;
  const isProviderNurseMode = location.pathname.startsWith("/provider/nurse/");
  const selectedFormCode = activeTabIndex === 1 ? PODIATRY_CONSENT : PODIATRY_TRACKER;

  const loadForms = async (nextSelectedFormId?: string, preferredFormCode?: string) => {
    if (!residentId) {
      return;
    }
    const response: any = await backendAPIStore.getNursePodiatristForms(residentId);
    const nextForms = Array.isArray(response?.forms) ? response.forms : [];
    setForms(nextForms);
    setQuickView(response?.quickView || {});
    const preferredCode = String(preferredFormCode || selectedFormCode || PODIATRY_TRACKER).trim().toUpperCase();
    const preferredForm = nextForms.find((item: any) => String(item?.formCode || "").trim().toUpperCase() === preferredCode) || null;
    const resolvedFormId = String(nextSelectedFormId || preferredForm?.formId || selectedFormId || nextForms[0]?.formId || "").trim();
    if (resolvedFormId) {
      const detailResponse: any = await backendAPIStore.getNursePodiatristFormRecords(resolvedFormId);
      setSelectedFormId(resolvedFormId);
      setSelectedFormHeader(detailResponse?.header || null);
      setFormData(detailResponse?.formData || {});
      setQuickView(detailResponse?.quickView || response?.quickView || {});
      setPreviousVisitComparison(detailResponse?.previousVisitComparison || null);
    } else {
      setSelectedFormId("");
      setSelectedFormHeader(null);
      setFormData({});
      setPreviousVisitComparison(null);
    }
  };

  useEffect(() => {
    const run = async () => {
      if (!residentId || !canAccess) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        await backendAPIStore.init();
        await loadForms(undefined, PODIATRY_TRACKER);
      } catch (error: any) {
        toast.current?.show({
          severity: "error",
          summary: getErrorTitle(error),
          detail: error?.message || "Unable to load documents.",
          life: 4000,
        });
      } finally {
        setLoading(false);
      }
    };
    void run();
  }, [backendAPIStore, canAccess, residentId]);

  useEffect(() => {
    const loadCurrentUserProfile = async () => {
      try {
        const userId = String(authStore.getUserId() || "").trim();
        if (!userId) {
          return;
        }
        const userProfile = await backendAPIStore.getUserProfile(userId);
        const fullName = `${String(userProfile?.firstName || "").trim()} ${String(userProfile?.lastName || "").trim()}`.trim();
        const nursePhone = String(userProfile?.phoneNumber || userProfile?.primaryPhoneNumber || userProfile?.telephoneNumber || "").trim();
        const nurseEmail = String(userProfile?.email || authStore.getUserEmailId?.() || "").trim();
        setCurrentUserFullName(fullName || String(authStore.getUserName?.() || "").trim());
        setCurrentNurseContactDetails(nursePhone);
        setCurrentNurseEmailAddress(nurseEmail);
      } catch {
        setCurrentUserFullName(String(authStore.getUserName?.() || "").trim());
        setCurrentNurseContactDetails("");
        setCurrentNurseEmailAddress(String(authStore.getUserEmailId?.() || "").trim());
      }
    };
    void loadCurrentUserProfile();
  }, [authStore, backendAPIStore]);

  const currentFormCode = String(selectedFormHeader?.formCode || "").trim().toUpperCase();
  const currentFormTitle = formMeta[currentFormCode]?.title || selectedFormHeader?.formTitle || "Form";
  const selectedFormSummary = forms.find((item: any) => String(item?.formCode || "").trim().toUpperCase() === selectedFormCode) || null;
  const sortFormVersions = (items: any[]) => [...items].sort((a: any, b: any) => {
    const versionDelta = Number(b?.version || 0) - Number(a?.version || 0);
    if (versionDelta !== 0) return versionDelta;
    return String(b?.updatedAt || b?.createdAt || "").localeCompare(String(a?.updatedAt || a?.createdAt || ""));
  });
  const selectedFormVersions = sortFormVersions(
    forms.filter((item: any) => String(item?.formCode || "").trim().toUpperCase() === selectedFormCode),
  );
  const trackerVersions = sortFormVersions(
    forms.filter((item: any) => String(item?.formCode || "").trim().toUpperCase() === PODIATRY_TRACKER),
  );
  const consentVersions = sortFormVersions(
    forms.filter((item: any) => String(item?.formCode || "").trim().toUpperCase() === PODIATRY_CONSENT),
  );

  const carePlanPath = useMemo(() => {
    if (!residentId) return "";
    if (isSharedResidentMode) {
      const pathParts = location.pathname.split("/");
      const sharedHomeId = String(pathParts[3] || "").trim();
      if (sharedHomeId) {
        return RouterAddress.getSharedResidentNcpPagePath(sharedHomeId, residentId);
      }
    }
    return isProviderNurseMode
      ? RouterAddress.getProviderNurseResidentNcpPagePath(residentId)
      : RouterAddress.getNurseResidentNcpPagePath(residentId);
  }, [isProviderNurseMode, isSharedResidentMode, location.pathname, residentId]);

  const onFieldChange = (fieldName: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const footFindingMarkers = useMemo<FootFindingMarker[]>(
    () => (
      Array.isArray(formData?.footFindingMarkers)
        ? formData.footFindingMarkers
          .map((item: any) => ({
            id: String(item?.id || "").trim(),
            view: String(item?.view || "").trim() as FootView,
            x: Number(item?.x),
            y: Number(item?.y),
            findingCode: String(item?.findingCode || "").trim().toUpperCase() as FootFindingCode,
          }))
          .filter((item: FootFindingMarker) =>
            !!item.id
            && Number.isFinite(item.x)
            && Number.isFinite(item.y)
            && FOOT_VIEW_OPTIONS.some((option) => option.view === item.view)
            && FOOT_FINDING_OPTIONS.some((option) => option.code === item.findingCode)
          )
        : []
    ),
    [formData?.footFindingMarkers],
  );

  const addFootFindingMarker = (view: FootView, x: number, y: number) => {
    const nextMarkers = [
      ...footFindingMarkers,
      {
        id: `foot-marker-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        view,
        x,
        y,
        findingCode: activeFootFindingCode,
      },
    ];
    setFormData((prev: any) => ({
      ...prev,
      footFindingMarkers: nextMarkers,
      observationFindingOptions: [
        ...normalizeSelectedValues(prev?.observationFindingOptions).filter((label) =>
          !Object.values(FINDING_LABEL_BY_CODE).includes(label as any),
        ),
        ...Array.from(new Set(nextMarkers.map((marker) => FINDING_LABEL_BY_CODE[marker.findingCode]))),
      ],
    }));
  };

  const removeFootFindingMarker = (markerId: string) => {
    const nextMarkers = footFindingMarkers.filter((marker) => marker.id !== markerId);
    setFormData((prev: any) => ({
      ...prev,
      footFindingMarkers: nextMarkers,
      observationFindingOptions: [
        ...normalizeSelectedValues(prev?.observationFindingOptions).filter((label) =>
          !Object.values(FINDING_LABEL_BY_CODE).includes(label as any),
        ),
        ...Array.from(new Set(nextMarkers.map((marker) => FINDING_LABEL_BY_CODE[marker.findingCode]))),
      ],
    }));
  };

  const clearFootFindingMarkers = () => {
    setFormData((prev: any) => ({
      ...prev,
      footFindingMarkers: [],
      observationFindingOptions: normalizeSelectedValues(prev?.observationFindingOptions).filter((label) =>
        !Object.values(FINDING_LABEL_BY_CODE).includes(label as any),
      ),
    }));
  };

  const previousTrackerFormData = previousVisitComparison?.formData || null;
  const previousTrackerHeader = previousVisitComparison?.header || null;
  const hasPreviousVisitComparisonData = previousTrackerFormData ? [
    previousTrackerFormData?.podiatristName,
    previousTrackerFormData?.followUpDate,
    previousTrackerFormData?.presentingConcernOptions,
    previousTrackerFormData?.changesSinceLastVisitDetails,
    previousTrackerFormData?.changesSinceLastVisitStatus,
    previousTrackerFormData?.interventionTreatment,
    previousTrackerFormData?.observationFindingOptions,
    previousTrackerFormData?.observationNotes,
    previousTrackerFormData?.observations,
    previousTrackerFormData?.followUpPlan,
  ].some((value) => hasMeaningfulComparisonValue(value)) : false;
  const currentFollowUpDate = String(formData?.followUpDate || "").trim();
  const isFollowUpDateInPast = !!currentFollowUpDate && currentFollowUpDate < TODAY_IN_LOS_ANGELES;
  const canCreateFollowUpReminder = currentFormCode === PODIATRY_TRACKER && !!currentFollowUpDate && !isFollowUpDateInPast;

  const openFollowUpReminderDialog = () => {
    if (!currentFollowUpDate) {
      toast.current?.show({
        severity: "warn",
        summary: "Follow-up Date Required",
        detail: "Add a follow-up date before creating a reminder.",
        life: 3500,
      });
      return;
    }
    if (isFollowUpDateInPast) {
      toast.current?.show({
        severity: "warn",
        summary: "Follow-up Date Is In The Past",
        detail: `Choose a follow-up date after ${formatDateOnlyDisplay(TODAY_IN_LOS_ANGELES)} to create a reminder.`,
        life: 4000,
      });
      return;
    }
    const residentName = String(formData?.residentName || quickView?.residentName || "").trim() || "Resident";
    const clinicianName = String(formData?.podiatristName || currentUserFullName || "").trim();
    const followUpDateLabel = formatDateOnlyDisplay(currentFollowUpDate);
    setFollowUpReminderConfig({
      title: `${residentName} podiatry follow-up`,
      description: `Foot and nail care follow-up for ${residentName} on ${followUpDateLabel}.${clinicianName ? ` Clinician: ${clinicianName}.` : ""}`,
      frequency: ReminderConstants.FREQUENCY_EVERY_MONTH,
      earlyWeeks: ReminderConstants.EARLY_REMINDER_1_WEEK,
    });
    setShowFollowUpReminderDialog(true);
  };

  const createFollowUpReminder = async () => {
    if (!residentId || !canCreateFollowUpReminder) {
      return;
    }
    setCreatingFollowUpReminder(true);
    try {
      await backendAPIStore.postReminder({
        role: "Nurse",
        priority: "Medium",
        title: String(followUpReminderConfig.title || "").trim(),
        description: String(followUpReminderConfig.description || "").trim(),
        startDate: currentFollowUpDate,
        frequency: followUpReminderConfig.frequency,
        earlyWeeks: followUpReminderConfig.earlyWeeks,
        timezone: LOS_ANGELES_TIMEZONE,
        setSampleReminder: false,
      });
      setShowFollowUpReminderDialog(false);
      toast.current?.show({
        severity: "success",
        summary: "Reminder Created",
        detail: "The follow-up reminder was added to your reminder workspace.",
        life: 3500,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to create the follow-up reminder.",
        life: 4000,
      });
    } finally {
      setCreatingFollowUpReminder(false);
    }
  };

  useEffect(() => {
    const desiredForm = forms.find((item: any) => String(item?.formCode || "").trim().toUpperCase() === selectedFormCode) || null;
    if (desiredForm?.formId && String(desiredForm.formId) !== String(selectedFormId || "")) {
      void loadForms(desiredForm.formId, selectedFormCode);
      return;
    }
    if (!desiredForm) {
      setSelectedFormId("");
      setSelectedFormHeader(null);
      setFormData({});
      setPreviousVisitComparison(null);
    }
  }, [activeTabIndex, forms]);

  useEffect(() => {
    if (
      currentFormCode === PODIATRY_TRACKER
      && (
        (currentUserFullName && !String(formData?.nurseSignatureName || "").trim())
        || (currentUserFullName && !String(formData?.nurseProfileFullName || "").trim())
        || (currentNurseContactDetails && !String(formData?.nurseProfileContactDetails || "").trim())
        || (currentNurseEmailAddress && !String(formData?.nurseProfileEmailAddress || "").trim())
      )
    ) {
      setFormData((prev: any) => ({
        ...prev,
        nurseSignatureName: String(prev?.nurseSignatureName || "").trim() || currentUserFullName,
        nurseProfileFullName: String(prev?.nurseProfileFullName || "").trim() || currentUserFullName,
        nurseProfileContactDetails: String(prev?.nurseProfileContactDetails || "").trim() || currentNurseContactDetails,
        nurseProfileEmailAddress: String(prev?.nurseProfileEmailAddress || "").trim() || currentNurseEmailAddress,
      }));
    }
  }, [
    currentFormCode,
    currentUserFullName,
    currentNurseContactDetails,
    currentNurseEmailAddress,
    formData?.nurseSignatureName,
    formData?.nurseProfileFullName,
    formData?.nurseProfileContactDetails,
    formData?.nurseProfileEmailAddress,
  ]);

  const renderDateField = (fieldName: string, label: string, columnClassName = "col-12 md:col-3") => (
    <div className={columnClassName}>
      <label className="block mb-2 font-semibold">{label}</label>
      <Calendar
        value={parseDateOnlyValue(formData?.[fieldName])}
        onChange={(event) => onFieldChange(fieldName, formatDateOnlyValue(event.value as Date | null))}
        showIcon
        dateFormat="mm/dd/yy"
        className="w-full"
      />
    </div>
  );

  const runGenerateFromCarePlan = async () => {
    if (!residentId) return;
    setCreatingDrafts(true);
    emitPodiatristActionLoading("generate-careplan", true);
    try {
      const response: any = await backendAPIStore.createNursePodiatristForms({
        residentId,
        generateFromCarePlan: true,
      });
      if (response?.success === false && response?.code === "CARE_PLAN_REQUIRED") {
        setShowCarePlanRequiredDialog(true);
        return;
      }

      const normalizedJobId = String(response?.jobId || "").trim();
      if (normalizedJobId) {
        setGenerationJobDetails({
          jobId: normalizedJobId,
          jobStatus: String(response?.jobStatus || JOB_STATUS.SUBMITTED).trim(),
          jobSubStatus: String(response?.jobSubStatus || "Queued").trim() || "Queued",
        });
        setGenerationProcessingTime(0);
        setShowGenerationJobDialog(true);

        const startedAt = Date.now();
        let currentJobDetails: any = null;
        let currentStatus = String(JOB_STATUS.SUBMITTED);
        do {
          currentJobDetails = await backendAPIStore.getJobDetails(normalizedJobId);
          setGenerationJobDetails(currentJobDetails);
          currentStatus = String(currentJobDetails?.jobStatus || JOB_STATUS.SUBMITTED).trim().toUpperCase();
          setGenerationProcessingTime(Math.max(1, Math.round((Date.now() - startedAt) / 1000)));

          if (currentStatus === JOB_STATUS.COMPLETED || currentStatus === JOB_STATUS.FAILED) {
            break;
          }
          await new Promise((resolve) => setTimeout(resolve, 2500));
        } while ((Date.now() - startedAt) / 1000 < 300);

        if (currentStatus !== JOB_STATUS.COMPLETED) {
          throw new Error(currentJobDetails?.errorMessage || "The foot and nail care job did not finish successfully.");
        }

        await loadForms(undefined, PODIATRY_TRACKER);
      } else {
        const nextForms = Array.isArray(response?.forms) ? response.forms : [];
        await loadForms(nextForms[0]?.formId, PODIATRY_TRACKER);
      }

      toast.current?.show({
        severity: "success",
        summary: "Forms Generated",
        detail: "Foot and nail care drafts were created from the resident care plan.",
        life: 3500,
      });
    } catch (error: any) {
      setGenerationJobDetails((current: any) => current ? ({
        ...current,
        jobStatus: JOB_STATUS.FAILED,
        jobSubStatus: String(error?.message || "Foot and nail care form generation failed"),
        errorMessage: String(error?.message || "Foot and nail care form generation failed"),
      }) : null);
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to create drafts.",
        life: 4000,
      });
    } finally {
      setCreatingDrafts(false);
      emitPodiatristActionLoading("generate-careplan", false);
    }
  };

  const generateFromCarePlan = () => {
    if (forms.length > 0) {
      confirmDialog({
        header: "Overwrite Existing Forms?",
        message: (
          <div style={{ lineHeight: 1.7 }}>
            <div style={{ marginBottom: "0.6rem" }}>
              You already created foot and nail care forms for this resident.
            </div>
            <div style={{ marginBottom: "0.6rem" }}>
              Generating again will create new drafts and may overwrite the form you are working on.
            </div>
            <div style={{ fontWeight: 600 }}>
              Do you want to continue?
            </div>
          </div>
        ),
        icon: "pi pi-exclamation-triangle",
        acceptClassName: "p-button-warning",
        acceptLabel: "Yes, Generate",
        rejectLabel: "Cancel",
        accept: () => {
          void runGenerateFromCarePlan();
        },
      });
      return;
    }
    void runGenerateFromCarePlan();
  };

  const saveDraft = async () => {
    if (!selectedFormId) return;
    setSaving(true);
    try {
      await backendAPIStore.updateNursePodiatristForm({
        formId: selectedFormId,
        formData,
        status: "draft",
      });
      await loadForms(selectedFormId, selectedFormCode);
      toast.current?.show({
        severity: "success",
        summary: "Draft Saved",
        detail: "Foot and nail care changes were saved.",
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to save the form.",
        life: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const markComplete = async () => {
    if (!selectedFormId) return;
    setMarkingComplete(true);
    try {
      await backendAPIStore.updateNursePodiatristForm({
        formId: selectedFormId,
        formData,
        status: "completed",
      });
      await loadForms(selectedFormId, selectedFormCode);
      toast.current?.show({
        severity: "success",
        summary: "Marked Complete",
        detail: "The foot and nail care form was marked as complete.",
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to mark the form as complete.",
        life: 4000,
      });
    } finally {
      setMarkingComplete(false);
    }
  };

  const startNewVersion = async (mode: "blank" | "copy", formCodeOverride?: string) => {
    if (!residentId) return;
    const targetFormCode = String(formCodeOverride || selectedFormCode || PODIATRY_TRACKER).trim().toUpperCase();
    const shouldEmitTrackerLoading = targetFormCode === PODIATRY_TRACKER;
    setCreatingVersion(true);
    try {
      const payload: any = {
        residentId,
        formCode: targetFormCode,
      };
      if (mode === "blank") {
        payload.createBlankForm = true;
      }
      if (mode === "copy") {
        const versionSource = targetFormCode === PODIATRY_CONSENT ? consentVersions : trackerVersions;
        const latestVersion = versionSource[0];
        const sourceFormId = String(latestVersion?.formId || "").trim();
        if (!sourceFormId) {
          throw new Error("No existing version is available to copy.");
        }
        payload.copyFromFormId = sourceFormId;
      }

      const response: any = await backendAPIStore.createNursePodiatristForms(payload);
      const nextForm = Array.isArray(response?.forms) ? response.forms[0] : null;
      await loadForms(String(nextForm?.formId || "").trim(), targetFormCode);
      toast.current?.show({
        severity: "success",
        summary: "New Version Created",
        detail: mode === "copy"
          ? "A new version was created from the last saved version."
          : "A new blank version was created.",
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to create a new version.",
        life: 4000,
      });
    } finally {
      setCreatingVersion(false);
      if (shouldEmitTrackerLoading) {
        emitPodiatristActionLoading("start-new-tracker", false);
      }
    }
  };

  const requestDocument = async () => {
    if (!selectedFormId) {
      throw new Error("Select a form first.");
    }
    await backendAPIStore.updateNursePodiatristForm({
      formId: selectedFormId,
      formData,
      status: "ready",
    });
    return await backendAPIStore.generateNursePodiatristDocument(selectedFormId);
  };

  const downloadDocument = async () => {
    setDownloadingDocument(true);
    try {
      const response: any = await requestDocument();
      const documentUrl = String(response?.documentUrl || "").trim();
      if (!documentUrl) {
        throw new Error("The generated document is missing a download URL.");
      }
      openUrlInNewTab(documentUrl);
      toast.current?.show({
        severity: "success",
        summary: "Download Ready",
        detail: "Document downloaded",
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to generate the document.",
        life: 4000,
      });
    } finally {
      setDownloadingDocument(false);
    }
  };

  const addToBinder = async () => {
    if (!residentId) return;
    setSavingToBinder(true);
    try {
      const response: any = await requestDocument();
      const outputKey = String(response?.outputKey || "").trim();
      const fileName = String(response?.fileName || "").trim();
      if (!outputKey || !fileName) {
        throw new Error("The generated document is missing binder save details.");
      }
      const bundle = await ensureResidentBinderBundle(backendAPIStore, residentId);
      const targetFolder = getBinderFolderByCode(bundle.folders, RESIDENT_BINDER_FOLDER_CODES.NURSE_DELEGATION);
      if (!bundle.binder?.binderId || !targetFolder?.folderId) {
        throw new Error("Resident binder folder is not available.");
      }
      await upsertBinderDocumentWithVersion({
        backendAPIStore,
        binderId: bundle.binder.binderId,
        folderId: targetFolder.folderId,
        folderName: targetFolder.folderName || "Nurse Delegation Documents",
        referenceId: residentId,
        title: currentFormTitle,
        fileName,
        mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        existingDocuments: bundle.documents,
        existingObjectKey: outputKey,
      });
      toast.current?.show({
        severity: "success",
        summary: "Saved to Binder",
        detail: "The document is saved to the resident binder.",
        life: 3500,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to save the foot and nail care document to the binder.",
        life: 4000,
      });
    } finally {
      setSavingToBinder(false);
    }
  };

  const renderFormActions = () => (
    <div className="flex gap-2 flex-wrap align-items-center">
      <Button
        type="button"
        label="Save"
        severity="warning"
        rounded
        outlined
        onClick={() => { void saveDraft(); }}
        loading={saving}
        disabled={!selectedFormSummary || downloadingDocument || savingToBinder || markingComplete}
      />
      <Button
        type="button"
        label="Download"
        severity="warning"
        rounded
        outlined
        icon="pi pi-download"
        onClick={() => { void downloadDocument(); }}
        loading={downloadingDocument}
        disabled={!selectedFormSummary || saving || savingToBinder || markingComplete}
      />
      <Button
        type="button"
        label="Add to Binder"
        severity="warning"
        rounded
        outlined
        icon="pi pi-bookmark"
        onClick={() => { void addToBinder(); }}
        loading={savingToBinder}
        disabled={!selectedFormSummary || saving || downloadingDocument || markingComplete}
      />
      <Button
        type="button"
        label="Mark Complete"
        severity="warning"
        rounded
        outlined
        icon="pi pi-check-circle"
        onClick={() => { void markComplete(); }}
        loading={markingComplete}
        disabled={!selectedFormSummary || saving || downloadingDocument || savingToBinder}
      />
    </div>
  );

  const renderComparisonField = (label: string, previousValue: any, currentValue: any) => (
    <div className="col-12 md:col-6">
      <div
        style={{
          border: "1px solid #e2e8f0",
          borderRadius: "14px",
          background: "#ffffff",
          padding: "0.9rem",
          height: "100%",
        }}
      >
        <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.6rem" }}>{label}</div>
        <div className="grid">
          <div className="col-12 md:col-6">
            <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#9a3412", marginBottom: "0.25rem" }}>Last Visit</div>
            <div style={{ color: "#475569", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{formatComparisonValue(previousValue)}</div>
          </div>
          <div className="col-12 md:col-6">
            <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#185da2", marginBottom: "0.25rem" }}>Current Draft</div>
            <div style={{ color: "#0f172a", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{formatComparisonValue(currentValue)}</div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderLastVisitComparison = () => {
    if (!previousTrackerFormData) {
      return null;
    }
    if (!hasPreviousVisitComparisonData) {
      return (
        <div className="col-12">
          <div style={{ ...appInnerCardStyle, padding: "1rem 1.1rem" }}>
            <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: "0.35rem" }}>Compare With Last Visit</div>
            <div style={{ ...appMutedTextStyle, lineHeight: 1.6 }}>
              Nothing to compare from previous visit.
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="col-12">
        <div style={{ ...appInnerCardStyle, padding: "1rem 1.1rem" }}>
          <div className="flex justify-content-between align-items-start gap-3 flex-wrap" style={{ marginBottom: "0.85rem" }}>
            <div>
              <div style={{ fontWeight: 800, color: "#0f172a" }}>Compare With Last Visit</div>
              <div style={{ ...appMutedTextStyle, marginTop: "0.35rem", lineHeight: 1.6 }}>
                Last visit from version {previousTrackerHeader?.version || "1"} on {formatDateOnlyDisplay(previousTrackerFormData?.dateOfService)}.
                {` `}Updated {formatDateTimeDisplay(previousTrackerHeader?.updatedAt || previousTrackerHeader?.createdAt)}.
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                type="button"
                label="Use Last Treatment"
                icon="pi pi-copy"
                rounded
                outlined
                severity="warning"
                onClick={() => onFieldChange("interventionTreatment", String(previousTrackerFormData?.interventionTreatment || "").trim())}
              />
              <Button
                type="button"
                label="Use Last Follow-Up Plan"
                icon="pi pi-copy"
                rounded
                outlined
                severity="warning"
                onClick={() => onFieldChange("followUpPlan", String(previousTrackerFormData?.followUpPlan || "").trim())}
              />
            </div>
          </div>
          <div className="grid">
            {renderComparisonField("Clinician / Foot Care Nurse", previousTrackerFormData?.podiatristName, formData?.podiatristName)}
            {renderComparisonField("Follow-up Date", formatDateOnlyDisplay(previousTrackerFormData?.followUpDate), formatDateOnlyDisplay(formData?.followUpDate))}
            {renderComparisonField("Presenting Concern", previousTrackerFormData?.presentingConcernOptions, formData?.presentingConcernOptions)}
            {renderComparisonField("Change Details", previousTrackerFormData?.changesSinceLastVisitDetails || previousTrackerFormData?.changesSinceLastVisitStatus, formData?.changesSinceLastVisitDetails || formData?.changesSinceLastVisitStatus)}
            {renderComparisonField("Intervention / Treatment", previousTrackerFormData?.interventionTreatment, formData?.interventionTreatment)}
            {renderComparisonField("Observed Findings", previousTrackerFormData?.observationFindingOptions, formData?.observationFindingOptions)}
            {renderComparisonField("Observation Notes", previousTrackerFormData?.observationNotes || previousTrackerFormData?.observations, formData?.observationNotes || formData?.observations)}
            {renderComparisonField("Follow-up Plan", previousTrackerFormData?.followUpPlan, formData?.followUpPlan)}
          </div>
        </div>
      </div>
    );
  };

  const currentFormStatus = String(selectedFormHeader?.status || selectedFormSummary?.status || "draft").trim().toLowerCase() || "draft";
  const currentFormStatusLabel = currentFormStatus === "completed" ? "Completed" : "Draft";
  const currentFormStatusStyle = currentFormStatus === "completed"
    ? { border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#166534" }
    : { border: "1px solid #fed7aa", background: "#fff7ed", color: "#9a3412" };

  const openVersion = async (formId: string, formCode: string) => {
    const normalizedFormId = String(formId || "").trim();
    if (!normalizedFormId) {
      return;
    }
    await loadForms(normalizedFormId, formCode);
  };

  const renderVersionList = (formCode: string, title: string, versions: any[]) => (
    <div style={appSectionCardStyle} className="mb-3">
      <div className="flex align-items-center justify-content-between mb-3">
        <div>
          <div className="font-semibold">{title}</div>
          <div style={appMutedTextStyle}>Open an earlier visit or prior version for this form type.</div>
        </div>
      </div>
      <div className="flex flex-column gap-2">
        {versions.map((versionItem: any) => {
          const isActive = String(versionItem?.formId || "") === String(selectedFormId || "");
          return (
            <div
              key={String(versionItem?.formId || Math.random())}
              className="flex flex-wrap align-items-center justify-content-between gap-3 p-2 border-round"
              style={{ border: "1px solid #e5e7eb", background: isActive ? "#fff7ed" : "#ffffff" }}
            >
              <div className="flex flex-column gap-1">
                <div className="font-semibold">
                  Version {versionItem?.version || 1}
                  {isActive ? " · Current" : ""}
                </div>
                <div style={appMutedTextStyle}>
                  Status: {String(versionItem?.status || "draft").trim().toLowerCase() === "completed" ? "Completed" : "Draft"}
                </div>
                <div style={appMutedTextStyle}>
                  Last updated: {formatDateTimeDisplay(versionItem?.updatedAt || versionItem?.createdAt)}
                </div>
              </div>
              <Button
                type="button"
                label={isActive ? "Open Current" : "Open"}
                text
                icon="pi pi-eye"
                onClick={() => {
                  setActiveTabIndex(formCode === PODIATRY_CONSENT ? 1 : 0);
                  void openVersion(String(versionItem?.formId || "").trim(), formCode);
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderEmptyState = (formLabel: string, formCode: string) => (
    <div
      style={{
        ...appInnerCardStyle,
        padding: "1.2rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.85rem",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <div style={{ ...appMutedTextStyle, color: "#334155", lineHeight: 1.7 }}>
        No {formLabel.toLowerCase()} draft yet. Start with a blank form or generate both forms from the resident care plan.
      </div>
      <div className="flex gap-2 flex-wrap justify-content-center">
        {!creatingDrafts ? (
          <Button
            type="button"
            label="Add New Blank Form"
            icon="pi pi-plus"
            rounded
            outlined
            severity="warning"
            onClick={() => { void startNewVersion("blank"); }}
            loading={creatingVersion && selectedFormCode === formCode}
          />
        ) : null}
        {!creatingVersion ? (
          <Button
            type="button"
            label="Generate From CarePlan"
            icon="pi pi-sparkles"
            rounded
            severity="warning"
            onClick={() => { void generateFromCarePlan(); }}
            loading={creatingDrafts}
          />
        ) : null}
      </div>
    </div>
  );

  useEffect(() => {
    const handleGenerateForms = () => {
      void generateFromCarePlan();
    };
    const handleStartNewTracker = () => {
      setActiveTabIndex(0);
      emitPodiatristActionLoading("start-new-tracker", true);
      void startNewVersion("blank", PODIATRY_TRACKER);
    };
    const handleCopyLastTracker = () => {
      setActiveTabIndex(0);
      emitPodiatristActionLoading("start-new-tracker", true);
      void startNewVersion("copy", PODIATRY_TRACKER);
    };
    if (typeof window !== "undefined") {
      window.addEventListener("resident-podiatrist:generate-forms", handleGenerateForms as EventListener);
      window.addEventListener("resident-podiatrist:start-new-tracker", handleStartNewTracker as EventListener);
      window.addEventListener("resident-podiatrist:copy-last-tracker", handleCopyLastTracker as EventListener);
      return () => {
        window.removeEventListener("resident-podiatrist:generate-forms", handleGenerateForms as EventListener);
        window.removeEventListener("resident-podiatrist:start-new-tracker", handleStartNewTracker as EventListener);
        window.removeEventListener("resident-podiatrist:copy-last-tracker", handleCopyLastTracker as EventListener);
      };
    }
    return undefined;
  }, [residentId, selectedFormCode]);

  const renderConsentEditor = () => (
    <div className="grid">
      <div className="col-12">
        <div style={{ ...appInnerCardStyle, padding: "1rem 1.1rem" }}>
          <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: "0.85rem" }}>General Intake Information</div>
          <div className="grid">
            <div className="col-12 md:col-6">
              <label className="block mb-2 font-semibold">Client Name</label>
              <InputText value={formData?.residentName || ""} onChange={(e) => onFieldChange("residentName", e.target.value)} className="w-full" />
            </div>
            {renderDateField("dateToday", "Date Today", "col-12 md:col-3")}
            {renderDateField("dateOfBirth", "Date of Birth", "col-12 md:col-3")}
            <div className="col-12 md:col-6">
              <label className="block mb-2 font-semibold">Residential Facility</label>
              <InputText value={formData?.facilityName || ""} onChange={(e) => onFieldChange("facilityName", e.target.value)} className="w-full" />
            </div>
            <div className="col-12 md:col-6">
              <label className="block mb-2 font-semibold">Address</label>
              <InputText value={formData?.addressLine1 || ""} onChange={(e) => onFieldChange("addressLine1", e.target.value)} className="w-full" />
            </div>
            <div className="col-12 md:col-4">
              <label className="block mb-2 font-semibold">Phone</label>
              <InputText value={formData?.phoneNumber || ""} onChange={(e) => onFieldChange("phoneNumber", e.target.value)} className="w-full" />
            </div>
            <div className="col-12 md:col-8">
              <label className="block mb-2 font-semibold">Emergency Contact / POA Name</label>
              <InputText value={formData?.emergencyContactName || ""} onChange={(e) => onFieldChange("emergencyContactName", e.target.value)} className="w-full" />
            </div>
            <div className="col-12 md:col-6">
              <label className="block mb-2 font-semibold">Relationship</label>
              <InputText value={formData?.emergencyContactRelationship || ""} onChange={(e) => onFieldChange("emergencyContactRelationship", e.target.value)} className="w-full" />
            </div>
            <div className="col-12 md:col-6">
              <label className="block mb-2 font-semibold">Contact Phone</label>
              <InputText value={formData?.emergencyContactPhone || ""} onChange={(e) => onFieldChange("emergencyContactPhone", e.target.value)} className="w-full" />
            </div>
          </div>
        </div>
      </div>

      <div className="col-12">
        <div style={{ ...appInnerCardStyle, padding: "1rem 1.1rem" }}>
          <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: "0.85rem" }}>Medical Intake</div>
          <div className="grid">
            <div className="col-12 md:col-6">
              <label className="block mb-2 font-semibold">Do you have diabetes?</label>
              <MultiSelect
                value={normalizeSelectedValues(formData?.diabetesStatusOptions)}
                options={resolveFormOptions(formData?.optionCatalog?.diabetesStatusOptions, ["No", "Yes", "Diet-Controlled", "Oral Meds", "Insulin", "Other"])}
                onChange={(event) => onFieldChange("diabetesStatusOptions", normalizeSelectedValues(event.value))}
                optionLabel="label"
                optionValue="value"
                placeholder="Select all that apply"
                filter
                display="chip"
                className="w-full"
                maxSelectedLabels={3}
              />
            </div>
            <div className="col-12 md:col-6">
              <label className="block mb-2 font-semibold">Current Foot Concerns</label>
              <MultiSelect
                value={normalizeSelectedValues(formData?.currentFootConcernOptions)}
                options={resolveFormOptions(formData?.optionCatalog?.currentFootConcernOptions, ["Elongated nails", "Callus / corns", "Pain", "Swelling", "Redness", "Dryness", "Ulcer / wound", "Fungal nail changes", "Ingrown nail", "Circulation concerns", "Other"])}
                onChange={(event) => onFieldChange("currentFootConcernOptions", normalizeSelectedValues(event.value))}
                optionLabel="label"
                optionValue="value"
                placeholder="Select all that apply"
                filter
                display="chip"
                className="w-full"
                maxSelectedLabels={3}
              />
            </div>
            <div className="col-12 md:col-3">
              <label className="block mb-2 font-semibold">Circulation Concerns</label>
              <Dropdown
                value={formData?.circulationConcernStatus || ""}
                options={resolveFormOptions(formData?.optionCatalog?.yesNoUnknownOptions, ["Yes", "No", "Unknown"])}
                onChange={(event) => onFieldChange("circulationConcernStatus", String(event.value || ""))}
                placeholder="Select one"
                className="w-full"
              />
            </div>
            <div className="col-12 md:col-3">
              <label className="block mb-2 font-semibold">Neuropathy / Loss of Sensation</label>
              <Dropdown
                value={formData?.neuropathyStatus || ""}
                options={resolveFormOptions(formData?.optionCatalog?.yesNoUnknownOptions, ["Yes", "No", "Unknown"])}
                onChange={(event) => onFieldChange("neuropathyStatus", String(event.value || ""))}
                placeholder="Select one"
                className="w-full"
              />
            </div>
            <div className="col-12 md:col-3">
              <label className="block mb-2 font-semibold">Open Wound / Ulcer</label>
              <Dropdown
                value={formData?.openWoundStatus || ""}
                options={resolveFormOptions(formData?.optionCatalog?.yesNoUnknownOptions, ["Yes", "No", "Unknown"])}
                onChange={(event) => onFieldChange("openWoundStatus", String(event.value || ""))}
                placeholder="Select one"
                className="w-full"
              />
            </div>
            <div className="col-12 md:col-3">
              <label className="block mb-2 font-semibold">On Blood Thinner / Anticoagulant</label>
              <Dropdown
                value={formData?.anticoagulantStatus || ""}
                options={resolveFormOptions(formData?.optionCatalog?.yesNoUnknownOptions, ["Yes", "No", "Unknown"])}
                onChange={(event) => onFieldChange("anticoagulantStatus", String(event.value || ""))}
                placeholder="Select one"
                className="w-full"
              />
            </div>
            <div className="col-12 md:col-6">
              <label className="block mb-2 font-semibold">Diabetes Other Details</label>
              <InputText value={formData?.diabetesOtherDetails || ""} onChange={(e) => onFieldChange("diabetesOtherDetails", e.target.value)} className="w-full" />
            </div>
            <div className="col-12 md:col-6">
              <label className="block mb-2 font-semibold">Other Foot Concern Details</label>
              <InputText value={formData?.currentFootConcernOtherDetails || ""} onChange={(e) => onFieldChange("currentFootConcernOtherDetails", e.target.value)} className="w-full" />
            </div>
            <div className="col-12 md:col-6">
              <label className="block mb-2 font-semibold">Diagnoses</label>
              <InputTextarea value={formData?.diagnosisText || ""} onChange={(e) => onFieldChange("diagnosisText", e.target.value)} rows={5} className="w-full" autoResize />
            </div>
            <div className="col-12 md:col-6">
              <label className="block mb-2 font-semibold">Allergies</label>
              <InputTextarea value={formData?.allergies || ""} onChange={(e) => onFieldChange("allergies", e.target.value)} rows={5} className="w-full" autoResize />
            </div>
            <div className="col-12">
              <label className="block mb-2 font-semibold">Foot Care Service Notes</label>
              <InputTextarea value={formData?.serviceNotes || ""} onChange={(e) => onFieldChange("serviceNotes", e.target.value)} rows={5} className="w-full" autoResize />
            </div>
          </div>
        </div>
      </div>

      <div className="col-12">
        <div style={{ ...appInnerCardStyle, padding: "1rem 1.1rem" }}>
          <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: "0.85rem" }}>Payment and Insurance</div>
          <div className="grid">
            <div className="col-12 md:col-7">
              <label className="block mb-2 font-semibold">Payment Method</label>
              <MultiSelect
                value={normalizeSelectedValues(formData?.paymentMethodOptions)}
                options={resolveFormOptions(formData?.optionCatalog?.paymentMethodOptions, ["Cash", "Check", "Credit/Debit Card", "Mobile Payment (Zelle, Cash App, etc.)", "Invoice / Pay Later"])}
                onChange={(event) => onFieldChange("paymentMethodOptions", normalizeSelectedValues(event.value))}
                optionLabel="label"
                optionValue="value"
                placeholder="Select payment method(s)"
                filter
                display="chip"
                className="w-full"
              />
            </div>
            <div className="col-12 md:col-2">
              <label className="block mb-2 font-semibold">Payment Received</label>
              <Dropdown
                value={formData?.paymentReceivedStatus || ""}
                options={resolveFormOptions(formData?.optionCatalog?.paymentReceivedOptions, ["Yes", "No"])}
                onChange={(event) => onFieldChange("paymentReceivedStatus", String(event.value || ""))}
                placeholder="Select one"
                className="w-full"
              />
            </div>
            <div className="col-12 md:col-3">
              <label className="block mb-2 font-semibold">Balance Due</label>
              <InputText value={formData?.balanceDue || ""} onChange={(e) => onFieldChange("balanceDue", e.target.value)} className="w-full" />
            </div>
            <div className="col-12">
              <div className="flex align-items-center gap-2 flex-wrap">
                <Checkbox
                  inputId="insuranceAcknowledged"
                  checked={formData?.insuranceAcknowledged === true}
                  onChange={(event) => onFieldChange("insuranceAcknowledged", event.checked === true)}
                />
                <label htmlFor="insuranceAcknowledged" style={{ cursor: "pointer" }}>
                  Client acknowledges that services are Private Pay and not billed to insurance.
                </label>
              </div>
              <div style={{ ...appMutedTextStyle, marginTop: "0.5rem" }}>
                Services provided by this Foot Care Nurse (Routine Foot Care) are not covered under insurance / Medicare and are considered an out-of-pocket expense.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="col-12">
        <div style={{ ...appInnerCardStyle, padding: "1rem 1.1rem" }}>
          <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: "0.85rem" }}>Photo Consent and Signature</div>
          <div className="grid">
            <div className="col-12 md:col-4">
              <label className="block mb-2 font-semibold">Photo Consent</label>
              <Dropdown
                value={formData?.photoConsentChoice || ""}
                options={resolveFormOptions(formData?.optionCatalog?.photoConsentOptions, ["Yes", "No"])}
                onChange={(event) => onFieldChange("photoConsentChoice", String(event.value || ""))}
                placeholder="Select one"
                className="w-full"
              />
            </div>
            <div className="col-12 md:col-3">
              <label className="block mb-2 font-semibold">Photo Consent Initials</label>
              <InputText value={formData?.photoConsentInitials || ""} onChange={(e) => onFieldChange("photoConsentInitials", e.target.value)} className="w-full" />
            </div>
            <div className="col-12">
              <div style={{ ...appMutedTextStyle, color: "#334155" }}>
                I consent to a comparison before / after photo(s) of my feet or legs if the nurse deems it advantageous to my care. There will be no personal identifiers in the photo and the nurse will maintain the photo in a confidential file.
              </div>
            </div>
            <div className="col-12 md:col-8">
              <label className="block mb-2 font-semibold">Signature of Client or Legal Representative</label>
              <InputText value={formData?.consentSignedBy || ""} onChange={(e) => onFieldChange("consentSignedBy", e.target.value)} className="w-full" />
            </div>
            {renderDateField("consentSignatureDate", "Signature Date", "col-12 md:col-4")}
          </div>
        </div>
      </div>
    </div>
  );

  const renderTrackerEditor = () => (
    <div className="grid">
      {renderLastVisitComparison()}
      <div className="col-12">
        <div style={{ ...appInnerCardStyle, padding: "1rem 1.1rem" }}>
          <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: "0.85rem" }}>Visit Information</div>
          <div className="grid">
            <div className="col-12 md:col-4">
              <label className="block mb-2 font-semibold">Resident Name</label>
              <InputText value={formData?.residentName || ""} onChange={(e) => onFieldChange("residentName", e.target.value)} className="w-full" />
            </div>
            <div className="col-12 md:col-4">
              <label className="block mb-2 font-semibold">Facility</label>
              <InputText value={formData?.facilityName || ""} onChange={(e) => onFieldChange("facilityName", e.target.value)} className="w-full" />
            </div>
            {renderDateField("dateOfService", "Date of Service", "col-12 md:col-4")}
            <div className="col-12 md:col-4">
              <label className="block mb-2 font-semibold">Clinician / Foot Care Nurse</label>
              <InputText value={formData?.podiatristName || ""} onChange={(e) => onFieldChange("podiatristName", e.target.value)} className="w-full" />
            </div>
            {renderDateField("followUpDate", "Follow-up Date", "col-12 md:col-4")}
            <div className="col-12 md:col-4">
              <label className="block mb-2 font-semibold">Visit Status</label>
              <Dropdown
                value={formData?.visitStatus || ""}
                options={resolveFormOptions(formData?.optionCatalog?.visitStatusOptions, ["Draft", "Completed", "Follow-up Needed", "Cancelled"])}
                onChange={(event) => onFieldChange("visitStatus", String(event.value || ""))}
                placeholder="Select one"
                className="w-full"
              />
            </div>
            <div className="col-12">
              <div
                style={{
                  border: "1px solid #fde68a",
                  borderRadius: "14px",
                  background: "linear-gradient(180deg, #fffdf5 0%, #fff7ed 100%)",
                  padding: "0.95rem 1rem",
                }}
              >
                <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                  <div>
                    <div style={{ fontWeight: 700, color: "#9a3412" }}>Follow-Up Reminder</div>
                    <div style={{ ...appMutedTextStyle, marginTop: "0.35rem", lineHeight: 1.6 }}>
                      Set a follow-up date and create a recurring reminder.
                    </div>
                    {currentFollowUpDate ? (
                      <div style={{ ...appMutedTextStyle, marginTop: "0.35rem", color: isFollowUpDateInPast ? "#b91c1c" : "#475569" }}>
                        Follow-up date: {formatDateOnlyDisplay(currentFollowUpDate)}
                        {isFollowUpDateInPast ? ` This date is in the past relative to Thursday, August 13, 2026.` : ""}
                      </div>
                    ) : null}
                  </div>
                  <Button
                    type="button"
                    label="Create Reminder"
                    icon="pi pi-bell"
                    rounded
                    severity="warning"
                    onClick={openFollowUpReminderDialog}
                    loading={creatingFollowUpReminder}
                    disabled={!currentFollowUpDate}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="col-12">
        <div style={{ ...appInnerCardStyle, padding: "1rem 1.1rem" }}>
          <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: "0.85rem" }}>Clinical Summary</div>
          <div className="grid">
            <div className="col-12 md:col-6">
              <label className="block mb-2 font-semibold">Diagnoses</label>
              <InputTextarea value={formData?.diagnosisText || ""} onChange={(e) => onFieldChange("diagnosisText", e.target.value)} rows={5} className="w-full" autoResize />
            </div>
            <div className="col-12 md:col-6">
              <label className="block mb-2 font-semibold">Allergies</label>
              <InputTextarea value={formData?.allergies || ""} onChange={(e) => onFieldChange("allergies", e.target.value)} rows={5} className="w-full" autoResize />
            </div>
          </div>
        </div>
      </div>
      <div className="col-12">
        <div style={{ ...appInnerCardStyle, padding: "1rem 1.1rem" }}>
          <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: "0.85rem" }}>Clinical Progress Note</div>
          <div className="grid">
            <div className="col-12 md:col-6">
              <label className="block mb-2 font-semibold">Client&apos;s Presenting Concern</label>
              <MultiSelect
                value={normalizeSelectedValues(formData?.presentingConcernOptions)}
                options={resolveFormOptions(formData?.optionCatalog?.presentingConcernOptions, ["Elongated nails", "Callus", "Corn", "Pain", "Other"])}
                onChange={(event) => onFieldChange("presentingConcernOptions", normalizeSelectedValues(event.value))}
                optionLabel="label"
                optionValue="value"
                placeholder="Select concern(s)"
                filter
                display="chip"
                className="w-full"
                maxSelectedLabels={3}
              />
            </div>
            <div className="col-12 md:col-6">
              <label className="block mb-2 font-semibold">Changes Since Last Visit</label>
              <Dropdown
                value={formData?.changesSinceLastVisitStatus || ""}
                options={resolveFormOptions(formData?.optionCatalog?.changesSinceLastVisitOptions, ["None", "Changes"])}
                onChange={(event) => onFieldChange("changesSinceLastVisitStatus", String(event.value || ""))}
                placeholder="Select one"
                className="w-full"
              />
            </div>
            <div className="col-12 md:col-6">
              <label className="block mb-2 font-semibold">Presenting Concern Details</label>
              <InputText
                value={formData?.presentingConcernOtherDetails || formData?.presentingConcern || ""}
                onChange={(e) => onFieldChange("presentingConcernOtherDetails", e.target.value)}
                className="w-full"
              />
            </div>
            <div className="col-12 md:col-6">
              <label className="block mb-2 font-semibold">Change Details</label>
              <InputText
                value={formData?.changesSinceLastVisitDetails || formData?.changesSinceLastVisit || ""}
                onChange={(e) => onFieldChange("changesSinceLastVisitDetails", e.target.value)}
                className="w-full"
              />
            </div>
            <div className="col-12">
              <label className="block mb-2 font-semibold">Intervention / Treatment</label>
              <InputTextarea value={formData?.interventionTreatment || ""} onChange={(e) => onFieldChange("interventionTreatment", e.target.value)} rows={5} className="w-full" autoResize />
            </div>
            <div className="col-12 md:col-6">
              <label className="block mb-2 font-semibold">Instruments Used</label>
              <MultiSelect
                value={normalizeSelectedValues(formData?.instrumentUsedOptions)}
                options={resolveFormOptions(formData?.optionCatalog?.instrumentUsedOptions, ["Nail nipper", "Rotary file / drill", "Curette", "Scalpel", "Foot file", "Other"])}
                onChange={(event) => onFieldChange("instrumentUsedOptions", normalizeSelectedValues(event.value))}
                optionLabel="label"
                optionValue="value"
                placeholder="Select instrument(s)"
                filter
                display="chip"
                className="w-full"
                maxSelectedLabels={3}
              />
            </div>
            <div className="col-12 md:col-6">
              <label className="block mb-2 font-semibold">Treatment Tolerance</label>
              <Dropdown
                value={formData?.toleranceStatus || ""}
                options={resolveFormOptions(formData?.optionCatalog?.toleranceOptions, ["1W", "S"])}
                onChange={(event) => onFieldChange("toleranceStatus", String(event.value || ""))}
                placeholder="Select one"
                className="w-full"
              />
              <div style={{ ...appMutedTextStyle, marginTop: "0.45rem" }}>
                1W = Tolerated Well, S = Sensitivity
              </div>
            </div>
            <div className="col-12 md:col-6">
              <label className="block mb-2 font-semibold">Instrument Details</label>
              <InputText
                value={formData?.instrumentUsedOtherDetails || ""}
                onChange={(e) => onFieldChange("instrumentUsedOtherDetails", e.target.value)}
                className="w-full"
              />
            </div>
            <div className="col-12 md:col-6">
              <label className="block mb-2 font-semibold">Observed Findings</label>
              <MultiSelect
                value={normalizeSelectedValues(formData?.observationFindingOptions)}
                options={resolveFormOptions(formData?.optionCatalog?.observationFindingOptions, ["C - Callus", "D - Dryness", "R - Redness", "U - Ulcer", "PU - Pre Ulcer", "F - Fissure", "M - Maceration", "S - Swelling", "T - Tinea"])}
                onChange={(event) => onFieldChange("observationFindingOptions", normalizeSelectedValues(event.value))}
                optionLabel="label"
                optionValue="value"
                placeholder="Select finding(s)"
                filter
                display="chip"
                className="w-full"
                maxSelectedLabels={3}
              />
            </div>
            <div className="col-12">
              <label className="block mb-2 font-semibold">Observation Notes</label>
              <InputTextarea
                value={formData?.observationNotes || formData?.observations || ""}
                onChange={(e) => onFieldChange("observationNotes", e.target.value)}
                rows={5}
                className="w-full"
                autoResize
              />
              <div style={{ ...appMutedTextStyle, marginTop: "0.45rem" }}>
                Findings key: C = Callus, D = Dryness, R = Redness, U = Ulcer, PU = Pre Ulcer, F = Fissure, M = Maceration, S = Swelling, T = Tinea.
              </div>
            </div>
            <div className="col-12">
              <div
                style={{
                  border: "1px solid #dbe4f0",
                  borderRadius: "16px",
                  background: "#f8fafc",
                  padding: "1rem",
                }}
              >
                <div className="flex justify-content-between align-items-start gap-3 flex-wrap" style={{ marginBottom: "0.9rem" }}>
                  <div>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>Foot Findings Map</div>
                    <div style={{ ...appMutedTextStyle, marginTop: "0.35rem", lineHeight: 1.6 }}>
                      Select a finding code, then click a foot image to place a marker. Click any marker to remove it.
                    </div>
                  </div>
                  <div className="flex gap-2 align-items-center flex-wrap">
                    <Dropdown
                      value={activeFootFindingCode}
                      options={FOOT_FINDING_OPTIONS.map((option) => ({
                        label: `${option.code} = ${option.label}`,
                        value: option.code,
                      }))}
                      onChange={(event) => setActiveFootFindingCode(String(event.value || "C") as FootFindingCode)}
                      className="w-full md:w-16rem"
                    />
                    <Button
                      type="button"
                      label="Clear Markers"
                      icon="pi pi-times"
                      outlined
                      severity="secondary"
                      onClick={clearFootFindingMarkers}
                      disabled={footFindingMarkers.length === 0}
                    />
                  </div>
                </div>
                <div className="grid">
                  {FOOT_VIEW_OPTIONS.map((viewOption) => (
                    <div key={viewOption.view} className="col-12 md:col-6">
                      <div
                        style={{
                          border: "1px solid #cbd5e1",
                          borderRadius: "16px",
                          background: "#ffffff",
                          padding: "0.85rem",
                        }}
                      >
                        <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.65rem" }}>{viewOption.title}</div>
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={(event) => {
                            const target = event.currentTarget;
                            const bounds = target.getBoundingClientRect();
                            const x = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width));
                            const y = Math.min(1, Math.max(0, (event.clientY - bounds.top) / bounds.height));
                            addFootFindingMarker(viewOption.view, x, y);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                            }
                          }}
                          style={{
                            position: "relative",
                            width: "100%",
                            maxWidth: "320px",
                            margin: "0 auto",
                            cursor: "crosshair",
                            borderRadius: "14px",
                            overflow: "hidden",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <img
                            src={viewOption.imageSrc}
                            alt={viewOption.title}
                            style={{
                              display: "block",
                              width: "100%",
                              height: "auto",
                              background: "#fff",
                            }}
                          />
                          {footFindingMarkers.filter((marker) => marker.view === viewOption.view).map((marker) => {
                            const option = FOOT_FINDING_OPTIONS.find((item) => item.code === marker.findingCode);
                            return (
                              <button
                                key={marker.id}
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  removeFootFindingMarker(marker.id);
                                }}
                                title={`${marker.findingCode} = ${FINDING_LABEL_BY_CODE[marker.findingCode].replace(`${marker.findingCode} - `, "")}`}
                                style={{
                                  position: "absolute",
                                  left: `${marker.x * 100}%`,
                                  top: `${marker.y * 100}%`,
                                  transform: "translate(-50%, -50%)",
                                  width: marker.findingCode.length > 1 ? "34px" : "30px",
                                  height: "30px",
                                  borderRadius: "999px",
                                  border: "2px solid rgba(15, 23, 42, 0.18)",
                                  background: option?.color || "#0f172a",
                                  color: option?.textColor || "#ffffff",
                                  fontSize: "0.78rem",
                                  fontWeight: 800,
                                  cursor: "pointer",
                                  boxShadow: "0 8px 18px rgba(15,23,42,0.18)",
                                }}
                              >
                                {marker.findingCode}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 flex-wrap" style={{ marginTop: "0.95rem" }}>
                  {FOOT_FINDING_OPTIONS.map((option) => {
                    const active = footFindingMarkers.some((marker) => marker.findingCode === option.code);
                    return (
                      <div
                        key={option.code}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.45rem",
                          borderRadius: "999px",
                          padding: "0.45rem 0.75rem",
                          border: active ? `1px solid ${option.color}` : "1px solid #cbd5e1",
                          background: active ? `${option.color}22` : "#ffffff",
                          color: "#0f172a",
                          fontSize: "0.86rem",
                          fontWeight: active ? 700 : 500,
                        }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: option.code.length > 1 ? "34px" : "28px",
                            height: "28px",
                            borderRadius: "999px",
                            background: option.color,
                            color: option.textColor,
                            fontWeight: 800,
                            fontSize: "0.76rem",
                          }}
                        >
                          {option.code}
                        </span>
                        <span>{option.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="col-12 md:col-7">
              <label className="block mb-2 font-semibold">Follow-up Plan</label>
              <InputTextarea value={formData?.followUpPlan || ""} onChange={(e) => onFieldChange("followUpPlan", e.target.value)} rows={4} className="w-full" autoResize />
            </div>
            <div className="col-12 md:col-5">
              <label className="block mb-2 font-semibold">Nurse Signature Name</label>
              <InputText value={formData?.nurseSignatureName || ""} onChange={(e) => onFieldChange("nurseSignatureName", e.target.value)} className="w-full" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (!canAccess) {
    return <Unauthorized />;
  }

  return (
    <div className="container-fluid px-3 py-3 nurse-resident-podiatrist-page p-component">
      <Toast ref={toast} />
      <style>
        {`
          .nurse-resident-podiatrist-page .resident-workspace-shell {
            width: 100%;
            max-width: 1440px;
            margin: 0 auto;
          }

          .nurse-resident-podiatrist-page .resident-workspace-header {
            width: 100%;
          }

          .nurse-resident-podiatrist-page .resident-workspace-tabs .p-tabview-nav-container {
            margin-bottom: 1rem;
          }

          .nurse-resident-podiatrist-page .resident-workspace-tabs {
            width: 100%;
          }

          .nurse-resident-podiatrist-page .resident-workspace-tabs .p-tabview-nav {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 0.55rem;
            padding: 0.45rem;
            border: 1px solid #dbe4f0;
            border-radius: 18px;
            background: linear-gradient(180deg, #f8fbff 0%, #f1f5f9 100%);
            box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
            width: 100%;
          }

          .nurse-resident-podiatrist-page .resident-workspace-tabs .p-tabview-nav li {
            margin: 0;
          }

          .nurse-resident-podiatrist-page .resident-workspace-tabs .p-tabview-nav li .p-tabview-nav-link {
            border: 1px solid #d7e0ea;
            border-radius: 14px;
            background: #ffffff;
            color: #475569;
            padding: 0.8rem 1rem;
            font-weight: 700;
            transition: all 0.18s ease;
            box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
          }

          .nurse-resident-podiatrist-page .resident-workspace-tabs .p-tabview-nav li .p-tabview-nav-link:not(.p-disabled):focus {
            box-shadow: 0 0 0 0.18rem rgba(249, 115, 22, 0.16);
          }

          .nurse-resident-podiatrist-page .resident-workspace-tabs .p-tabview-nav li:not(.p-highlight) .p-tabview-nav-link:hover {
            border-color: #fdba74;
            background: linear-gradient(180deg, #ffffff 0%, #fff7ed 100%);
            color: #9a3412;
            transform: translateY(-1px);
          }

          .nurse-resident-podiatrist-page .resident-workspace-tabs .p-tabview-nav li.p-highlight .p-tabview-nav-link {
            border-color: #fdba74;
            background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 55%, #fef3c7 100%);
            color: #0f172a;
            box-shadow: 0 8px 18px rgba(249, 115, 22, 0.14);
          }

          .nurse-resident-podiatrist-page .resident-workspace-tabs .p-tabview-panels {
            padding: 0;
            background: transparent;
          }

          .nurse-resident-podiatrist-page .resident-workspace-tabs .p-tabview-panels .p-tabview-panel {
            display: flex;
            flex-direction: column;
            align-items: stretch;
          }

          .nurse-resident-podiatrist-page .resident-workspace-tabs .p-tabview-panels .p-tabview-panel > * {
            width: 100%;
          }

        `}
      </style>
      <div style={dashboardPageContainerStyle}>
        <div className="resident-workspace-header">
          {renderDashboardHeader(
            `${FOOT_NAIL_CARE_TITLE}${quickView?.residentName ? ` - ${quickView.residentName}` : ""}`,
            "Manage the consent form and tracker from one workspace.",
          )}
        </div>
        <div className="resident-workspace-shell">
          {loading ? (
            <div className="flex justify-content-center" style={{ padding: "2rem" }}>
              <ProgressSpinner />
            </div>
          ) : (
            <>
              <TabView
                activeIndex={activeTabIndex}
                onTabChange={(event) => setActiveTabIndex(event.index)}
                className="resident-workspace-tabs"
              >
                <TabPanel header="Current Progress">
                  <div style={{ ...appSectionCardStyle, marginTop: "0.25rem", padding: "1rem 1.1rem" }}>
                    <div className="flex justify-content-between align-items-center gap-3 flex-wrap" style={{ marginBottom: "1rem" }}>
                      <div>
                        <div className="flex align-items-center gap-2 flex-wrap">
                          <div style={{ fontWeight: 800, color: "#0f172a" }}>Current Progress</div>
                          <span
                            style={{
                              ...currentFormStatusStyle,
                              borderRadius: "999px",
                              padding: "0.2rem 0.65rem",
                              fontSize: "0.78rem",
                              fontWeight: 700,
                            }}
                          >
                            {currentFormStatusLabel}
                          </span>
                        </div>
                        <div style={{ color: "#64748b", marginTop: "0.35rem" }}>
                          Review, update, complete, download, or add the tracker to binder.
                        </div>
                      </div>
                      {renderFormActions()}
                    </div>
                    {selectedFormSummary ? (
                      <>
                        {renderTrackerEditor()}
                        <div className="flex justify-content-end mt-3">
                          {renderFormActions()}
                        </div>
                      </>
                    ) : (
                      renderEmptyState("Current Progress", PODIATRY_TRACKER)
                    )}
                  </div>
                </TabPanel>
                <TabPanel header="Consent Form">
                  <div style={{ ...appSectionCardStyle, marginTop: "0.25rem", padding: "1rem 1.1rem" }}>
                    <div className="flex justify-content-between align-items-center gap-3 flex-wrap" style={{ marginBottom: "1rem" }}>
                      <div>
                        <div className="flex align-items-center gap-2 flex-wrap">
                          <div style={{ fontWeight: 800, color: "#0f172a" }}>Consent Form</div>
                          <span
                            style={{
                              ...currentFormStatusStyle,
                              borderRadius: "999px",
                              padding: "0.2rem 0.65rem",
                              fontSize: "0.78rem",
                              fontWeight: 700,
                            }}
                          >
                            {currentFormStatusLabel}
                          </span>
                        </div>
                        <div style={{ color: "#64748b", marginTop: "0.35rem" }}>
                          Review, update, complete, download, or add the consent form to binder.
                        </div>
                      </div>
                      {renderFormActions()}
                    </div>
                    {selectedFormSummary ? (
                      <>
                        {renderConsentEditor()}
                        <div className="flex justify-content-end mt-3">
                          {renderFormActions()}
                        </div>
                      </>
                    ) : (
                      renderEmptyState("Consent Form", PODIATRY_CONSENT)
                    )}
                  </div>
                </TabPanel>
                <TabPanel header="View Previous Visits">
                  <div style={{ ...appSectionCardStyle, marginTop: "0.25rem", padding: "1rem 1.1rem" }}>
                    <div style={{ marginBottom: "1rem" }}>
                      <div style={{ fontWeight: 800, color: "#0f172a" }}>View Previous Visits</div>
                      <div style={{ color: "#64748b", marginTop: "0.35rem" }}>
                        Review older current-progress and consent form visits, then reopen any version in its editing tab.
                      </div>
                    </div>
                    {trackerVersions.length === 0 && consentVersions.length === 0 ? (
                      <div style={{ ...appMutedTextStyle, color: "#334155" }}>
                        No visits are recorded yet.
                      </div>
                    ) : (
                      <>
                        {trackerVersions.length > 0 ? renderVersionList(PODIATRY_TRACKER, "Current Progress Visits", trackerVersions) : null}
                        {consentVersions.length > 0 ? renderVersionList(PODIATRY_CONSENT, "Consent Form Visits", consentVersions) : null}
                      </>
                    )}
                  </div>
                </TabPanel>
              </TabView>
            </>
          )}
        </div>
      </div>

      <Dialog
        visible={showFollowUpReminderDialog}
        onHide={() => setShowFollowUpReminderDialog(false)}
        header="Create Follow-Up Reminder"
        style={{ width: "38rem", maxWidth: "95vw" }}
        footer={(
          <div className="flex justify-content-end gap-2">
            <Button
              type="button"
              label="Cancel"
              severity="secondary"
              outlined
              rounded
              onClick={() => setShowFollowUpReminderDialog(false)}
              disabled={creatingFollowUpReminder}
            />
            <Button
              type="button"
              label="Create Reminder"
              icon="pi pi-bell"
              rounded
              severity="warning"
              onClick={() => { void createFollowUpReminder(); }}
              loading={creatingFollowUpReminder}
            />
          </div>
        )}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <div style={{ ...appInnerCardStyle, padding: "1rem" }}>
            <div className="grid" style={{ marginTop: "0.25rem" }}>
              <div className="col-12 md:col-12">
                <label className="block mb-2 font-semibold">Follow-up Date</label>
                <strong>{formatDateOnlyDisplay(currentFollowUpDate)} - {LOS_ANGELES_TIMEZONE}</strong>
              </div>

              <div className="col-12">
                <label className="block mb-2 font-semibold">Reminder Title</label>
                <InputText
                  value={followUpReminderConfig.title}
                  onChange={(event) => setFollowUpReminderConfig((current) => ({ ...current, title: event.target.value }))}
                  className="w-full"
                />
              </div>
              <div className="col-12">
                <label className="block mb-2 font-semibold">Description</label>
                <InputTextarea
                  value={followUpReminderConfig.description}
                  onChange={(event) => setFollowUpReminderConfig((current) => ({ ...current, description: event.target.value }))}
                  rows={4}
                  className="w-full"
                  autoResize
                />
              </div>
              <div className="col-12 md:col-6">
                <label className="block mb-2 font-semibold">Repeat Frequency</label>
                <Dropdown
                  value={followUpReminderConfig.frequency}
                  options={ReminderConstants.REMINDER_FREQUENCY}
                  onChange={(event) => setFollowUpReminderConfig((current) => ({ ...current, frequency: String(event.value || "") }))}
                  className="w-full"
                />
              </div>
              <div className="col-12 md:col-6">
                <label className="block mb-2 font-semibold">Reminder Lead Time</label>
                <Dropdown
                  value={followUpReminderConfig.earlyWeeks}
                  options={ReminderConstants.EARLY_REMINDER_WEEKS}
                  onChange={(event) => setFollowUpReminderConfig((current) => ({ ...current, earlyWeeks: String(event.value || "") }))}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={showGenerationJobDialog}
        onHide={() => setShowGenerationJobDialog(false)}
        header="Foot & Nail Care Generation"
        style={{ width: "42rem", maxWidth: "95vw" }}
        footer={(
          <p style={{ margin: 0, fontSize: "0.82rem", color: "#64748b", fontStyle: "italic" }}>
            {AI_MESSAGE_FOR_COMPLIANCE}
          </p>
        )}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {String(generationJobDetails?.jobStatus || "").trim().toUpperCase() !== JOB_STATUS.COMPLETED
            && String(generationJobDetails?.jobStatus || "").trim().toUpperCase() !== JOB_STATUS.FAILED ? (
            <div className="col-12 text-center d-flex justify-content-center align-items-center">
              <div>
                <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
                <div className="block mt-3" style={{ fontWeight: 700, color: "#0f172a" }}>
                  {generationJobDetails?.jobSubStatus || "Generating forms"}
                </div>
                <div className="block mt-2" style={appMutedTextStyle}>
                  {generationProcessingTime > 0
                    ? `Processing... ${generationProcessingTime} seconds elapsed`
                    : "Starting the generation job..."}
                </div>
              </div>
            </div>
          ) : String(generationJobDetails?.jobStatus || "").trim().toUpperCase() === JOB_STATUS.FAILED ? (
            <div style={{ color: "#b91c1c", lineHeight: 1.6 }}>
              {generationJobDetails?.errorMessage || generationJobDetails?.jobSubStatus || "Foot and nail care generation failed."}
            </div>
          ) : (
            <div
              style={{
                border: "1px solid #dbe7f3",
                borderRadius: "14px",
                background: "linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)",
                padding: "1rem",
              }}
            >
              <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>
                Forms ready
              </div>
              <div style={{ ...appMutedTextStyle, lineHeight: 1.6 }}>
                Foot and nail care drafts finished generating. Review both forms before downloading, adding to binder, or marking them complete.
              </div>
            </div>
          )}
        </div>
      </Dialog>

      <Dialog
        visible={showCarePlanRequiredDialog}
        onHide={() => setShowCarePlanRequiredDialog(false)}
        header="Care Plan Required"
        style={{ width: "32rem", maxWidth: "95vw" }}
      >
        <div style={{ color: "#475569", lineHeight: 1.7 }}>
          Generate the resident care plan first. This workflow uses diagnosis and allergy details from that source.
        </div>
        <div className="flex justify-content-end gap-2" style={{ marginTop: "1rem" }}>
          <Button type="button" label="Close" text onClick={() => setShowCarePlanRequiredDialog(false)} />
          <Button
            type="button"
            label="Go to Care Plan"
            severity="warning"
            rounded
            onClick={() => {
              setShowCarePlanRequiredDialog(false);
              if (carePlanPath) {
                navigate(carePlanPath);
              }
            }}
          />
        </div>
      </Dialog>
    </div>
  );
};

export default NurseResidentPodiatristPage;
